import React, { useState, useCallback, useRef, useEffect } from 'react';

import type ItemList from '@this/domain/approve_item/item_list';
import SelectApproveItemListList from './select_approveitemlist_list/select_approveitemlist_list';

interface Props {
  approveItemId: number;
  chargingDepartmentIds?: number[];
  defaultItemListName: string | undefined;
  onSelect: (itemList: ItemList | null) => void;
  tripedit?: boolean;
  workflowFormWidth: string;
}

interface TruncatableTextProps {
  text: string;
  truncateMaxWidth: string;
}

interface HTMLElementEvent<T extends EventTarget> extends Event {
  target: T;
}

const useTruncated = (
  text?: string | undefined | null,
  truncateMaxWidth?: number
): [(node: HTMLDivElement | null) => void, boolean | undefined] => {
  const [truncated, setTruncated] = useState(false);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      if (truncateMaxWidth) {
        setTruncated(node.scrollWidth > truncateMaxWidth);
      } else {
        setTruncated(node.scrollWidth > node.offsetWidth);
      }
    },
    [truncateMaxWidth]
  );
  return [ref, truncated];
};

const TruncatableText: React.FC<TruncatableTextProps> = ({ text, truncateMaxWidth }) => {
  const maxWidth = truncateMaxWidth ? Number(truncateMaxWidth.replace(/[^0-9]/g, '')) : 420;
  const [ref, isTruncated] = useTruncated(text, maxWidth);
  const title = isTruncated ? { title: text } : {};

  return (
    <div
      ref={ref}
      {...title}
      style={{
        height: '30px',
        width: '450px',
        background: '#fff',
        lineHeight: '30px',
        padding: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }}
    >
      {text}
    </div>
  );
};

const SelectApproveItemList: React.FC<Props> = ({
  approveItemId,
  chargingDepartmentIds,
  defaultItemListName,
  onSelect,
  tripedit,
  workflowFormWidth
}) => {
  const [showList, setShowList] = useState(false);
  const [itemList, setItemList] = useState<ItemList | null>(null);
  const selectAreaRef = useRef<HTMLDivElement>(null);

  const handleClickEvent = useCallback((e: HTMLElementEvent<HTMLElement>) => {
    if (selectAreaRef.current && !selectAreaRef.current.contains(e.target as Node)) {
      setShowList(false);
    }
  }, []);

  useEffect(() => {
    const clickHandler = {
      handleEvent: (e: HTMLElementEvent<HTMLElement>) => {
        handleClickEvent(e);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [handleClickEvent]);

  const handleSelect = useCallback(
    (selectedItemList: ItemList | null) => {
      setShowList(false);
      setItemList(selectedItemList);
      onSelect(selectedItemList);
    },
    [onSelect]
  );

  const handleToggleShowList = useCallback(() => {
    setShowList(prev => !prev);
  }, []);

  const getDefaultItemListName = (): string => {
    if (defaultItemListName !== null && defaultItemListName !== undefined) {
      return defaultItemListName;
    }
    return '';
  };

  try {
    return (
      <div ref={selectAreaRef} style={{ margin: 0 }}>
        <div
          onClick={handleToggleShowList}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleShowList();
            }
          }}
          role="button"
          tabIndex={0}
          style={{
            minWidth: '50px',
            width: workflowFormWidth || '420px',
            flexWrap: 'nowrap',
            display: 'flex',
            border: '2px solid #eee',
            padding: '0px 10px',
            cursor: 'pointer',
            lineHeight: 'normal'
          }}
        >
          {itemList ? (
            <TruncatableText text={`${itemList.code}：${itemList.name}`} truncateMaxWidth={workflowFormWidth} />
          ) : (
            <TruncatableText text={getDefaultItemListName()} truncateMaxWidth={workflowFormWidth} />
          )}
          <div
            style={{
              height: '30px',
              width: '15px',
              background: '#fff',
              lineHeight: '30px',
              padding: 0,
              cursor: 'pointer',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}
          >
            <span style={{ marginLeft: 'auto' }}>{showList ? '▲' : '▼'}</span>
          </div>
        </div>
        {showList && (
          <SelectApproveItemListList
            self={itemList}
            onSelect={handleSelect}
            approveItemId={approveItemId}
            chargingDepartmentIds={chargingDepartmentIds}
            tripedit={tripedit}
            workflowFormWidth={workflowFormWidth}
          />
        )}
      </div>
    );
  } catch (e) {
    return null;
  }
};

export default SelectApproveItemList;
